class YoutubeVideos extends HTMLElement {
  connectedCallback() {
    this.sliderEl = this.querySelector('[data-youtube-slider]');
    this.modal = this.querySelector('[data-youtube-modal]');

    this.onKeydown = this.onKeydown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onModalClick = this.onModalClick.bind(this);

    this.addEventListener('click', this.onClick);

    // Move modal to <body> so it escapes the section's stacking context
    // (the section uses `isolation: isolate` + `overflow: hidden`).
    if (this.modal && this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }
    this.player = this.modal?.querySelector('[data-youtube-player]');
    this.modal?.addEventListener('click', this.onModalClick);

    if (this.sliderEl) {
      if (typeof Splide === 'undefined') {
        document
          .querySelector('script[src*="splide"]')
          ?.addEventListener('load', () => this.initSplide());
      } else {
        this.initSplide();
      }
    }
  }

  disconnectedCallback() {
    this.splide?.destroy();
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.removeProperty('overflow');
    this.setOutsideInert(false); // undo any inert if removed while open
    // Modal was moved to <body>; clean it up so it doesn't outlive the section.
    this.modal?.removeEventListener('click', this.onModalClick);
    this.modal?.remove();
  }

  initSplide() {
    this.splide = new Splide(this.sliderEl, {
      type: 'loop',
      role: 'region',
      label: 'Videos',
      // Make the slider focusable (tabindex 0) and navigable with arrow keys.
      keyboard: 'focused',
      perPage: 3,
      perMove: 1,
      gap: '24px',
      arrows: false,
      pagination: false,
      drag: true,
      focus: 'center',
      trimSpace: false,
      breakpoints: {
        989: {
          perPage: 2,
          gap: '16px',
        },
        749: {
          perPage: 1,
          gap: '6px',
        },
      },
    });

    this.splide.mount();
  }

  onClick(event) {
    const link = event.target.closest('.youtube-video-card__link');
    if (!link) return;

    const id = this.extractVideoId(link.getAttribute('href'));
    if (!id) return; // unknown URL — let the browser navigate as fallback

    event.preventDefault();
    this.openModal(id);
  }

  onModalClick(event) {
    if (event.target.closest('[data-youtube-close]')) {
      event.preventDefault();
      this.closeModal();
      return;
    }
    // Click on the fullscreen backdrop (anywhere outside the player) closes too.
    if (!event.target.closest('[data-youtube-player]')) {
      this.closeModal();
    }
  }

  extractVideoId(url) {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
    );
    return match ? match[1] : null;
  }

  openModal(videoId) {
    if (!this.modal || !this.player) return;

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
    iframe.title = 'YouTube video player';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.setAttribute('frameborder', '0');

    // Remember what to restore focus to when the modal closes.
    this.previouslyFocused = document.activeElement;

    this.player.replaceChildren(iframe);
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    // Make everything outside the modal non-focusable / hidden from AT so focus
    // is confined to the dialog.
    this.setOutsideInert(true);
    document.addEventListener('keydown', this.onKeydown);

    const closeBtn = this.modal.querySelector('.youtube-videos__modal-close');
    closeBtn?.focus();
  }

  closeModal() {
    if (!this.modal || this.modal.hidden) return;
    this.player.replaceChildren(); // removes iframe → stops video + frees memory
    this.modal.hidden = true;
    document.body.style.removeProperty('overflow');
    this.setOutsideInert(false);
    document.removeEventListener('keydown', this.onKeydown);

    // Return focus to the trigger (the video card link).
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
    this.previouslyFocused = null;
  }

  // Toggle `inert` + `aria-hidden` on every <body> child except the modal.
  setOutsideInert(on) {
    if (on) {
      this.inertedEls = Array.from(document.body.children).filter(
        (el) => el !== this.modal && !el.hasAttribute('inert')
      );
      this.inertedEls.forEach((el) => {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      });
    } else if (this.inertedEls) {
      this.inertedEls.forEach((el) => {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      });
      this.inertedEls = null;
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeModal();
  }
}

customElements.define('youtube-videos', YoutubeVideos);
